import { Box, Button, Chip, TextField, Typography } from '@mui/material';
import LabeledBox from '../../special/LabeledBox';
import { useEffect, useState } from 'react';
import ExoDialog from '../../exo/ExoDialog';
import { useTranslation } from 'react-i18next';
import ExoTabs from '../../exo/ExoTabs';
import ContactsIcon from '@mui/icons-material/Contacts';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import SelectCompanies from '../../select/SelectCompanies';
import SelectContacts from '../../select/SelectContacts';
import SelectEmployees from '../../select/SelectEmployees';
import SelectLeadPersons from '../../select/SelectLeadPersons';
import SelectLeadCompanies from '../../select/SelectLeadCompanies';
import { SelectFromNameValueArray } from '../../select/SelectFromNameValueArray';
import Feedback from '../../special/Feedback';

const TypeMailSelect = ({
  field,
  dataCollection,
  errors = {},
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false); // State to control dialog visibility
  const [selection, setSelection] = useState(dataCollection[field.key] || []); // State to store the selected emails

  // Handle selection change and notify parent component
  function handleSelectionChange(value) {
    updateValidationOnChange([field.key], value);
    setSelection(value);
  }

  return (
    <>
      <LabeledBox
        className="col-span-2"
        onClick={() => setOpen(true)} // Open the dialog when the label is clicked
        label={field.label}
      >
        {/* Display message if no emails are selected */}
        {selection.length === 0 && t('No CC Selected')}
        {selection.map((item, index) => (
          <MailItem
            item={item}
            key={`${item.type}-${item.id || item.email}-${item.email}-${index}`}
          /> // Display selected emails
        ))}
      </LabeledBox>
      <ExoDialog open={open} limitWidth>
        <SelectMail
          label={field.label}
          onCancel={() => setOpen(false)} // Close dialog on cancel
          onSave={handleSelectionChange} // Save selected emails
          startData={selection} // Pass initial selected data
        />
      </ExoDialog>
    </>
  );
};

// Display an individual email item
const MailItem = ({ item, onDelete = () => {} }) => {
  return (
    <Typography onClick={() => onDelete(item)}>
      <b>{item.name}</b> {`<${item.email}>`}
    </Typography>
  );
};

// The dialog component to select emails
const SelectMail = ({ label, onCancel, onSave, startData }) => {
  const { t } = useTranslation();
  const [deliveryCCData, setDeliveryCCData] = useState([]); // Stores selected email data
  const [nameValueData, setNameValueData] = useState({}); // Stores data for email selection dialog
  const [elementSave, setElementSave] = useState(null); // Stores the current element being modified

  const [selection, setSelection] = useState({
    company: [],
    contact: [],
    employee: [],
    leadPerson: [],
    leadCompany: [],
    custom: [],
  });
  // Set pre-selected emails
  const [preSelected, setPreSelected] = useState({
    company: [],
    contact: [],
    employee: [],
    leadPerson: [],
    leadCompany: [],
    custom: [],
  });
  useEffect(() => {
    const newSelection = {
      company: startData.filter((obj) => obj.type === 'company'),
      contact: startData.filter((obj) => obj.type === 'contact'),
      employee: startData.filter((obj) => obj.type === 'employee'),
      leadPerson: startData.filter((obj) => obj.type === 'leadPerson'),
      leadCompany: startData.filter((obj) => obj.type === 'leadCompany'),
      custom: startData.filter((obj) => obj.type === 'custom'),
    };

    setSelection(newSelection);
    setPreSelected(newSelection);
  }, [startData]);

  // Remove duplicates from selected items based on ID
  function getUniqueById(array) {
    const seen = new Set();
    return array.filter((item) => {
      if (!item.id) return true;
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  // Open the email dialog with pre-selected emails for a specific entity
  function openEmailDialog(element, type) {
    const alreadySelectedEmails = selection[type]
      .filter((item) => item.id === element.id)
      .map((item) => item.email);

    setElementSave({ element, type });

    setNameValueData({
      array: element.emails || [],
      nameKey: 'emailType',
      valueKey: 'emailAddress',
      selectedValues: alreadySelectedEmails, // Pass selected emails for highlighting
    });
  }

  // Update the selected emails for a given entity
  function updateEntityEmails(type, element, selectedEmails) {
    const existingWithoutCurrentEntity = selection[type].filter(
      (item) => item.id !== element.id,
    );

    const newItems = selectedEmails.map((email) => ({
      ...element,
      email,
    }));

    const newSelection = {
      ...selection,
      [type]: [...existingWithoutCurrentEntity, ...newItems],
    };

    setSelection(newSelection); // Update the selection
    setPreSelected(newSelection); // Update pre-selected data
    setNameValueData({}); // Clear email selection data
    setElementSave(null); // Reset element
  }

  // Keep only the selected entities by ID
  function keepOnlySelectedEntities(type, selectedEntities) {
    const selectedIds = selectedEntities.map((item) => item.id);
    return selection[type].filter((item) => selectedIds.includes(item.id));
  }

  // Tabs for selecting different types of entities (Companies, Contacts, etc.)
  const tabs = [
    {
      label: t('Companies'),
      content: (
        <SelectCompanies
          onSelect={handleCompanysSelect}
          dataCollection={getUniqueById(selection.company)} // Remove duplicates
        />
      ),
      icon: <ApartmentIcon />,
    },
    {
      label: t('Contacts'),
      content: (
        <SelectContacts
          onSelect={handleContactsSelect}
          dataCollection={getUniqueById(selection.contact)} // Remove duplicates
        />
      ),
      icon: <ContactsIcon />,
    },
    {
      label: t('Employees'),
      content: (
        <SelectEmployees
          onSelect={handleEmployeesSelect}
          preSelected={preSelected.employee} // Pass pre-selected employees
        />
      ),
      icon: <PeopleOutlinedIcon />,
    },
    {
      label: t('Lead Persons'),
      content: (
        <SelectLeadPersons
          onSelect={handleLeadPersonsSelect}
          dataCollection={getUniqueById(selection.leadPerson)} // Remove duplicates
        />
      ),
      icon: <PersonSearchIcon />,
    },
    {
      label: t('Lead Companies'),
      content: (
        <SelectLeadCompanies
          onSelect={handleLeadCompaniesSelect}
          dataCollection={getUniqueById(selection.leadCompany)} // Remove duplicates
        />
      ),
      icon: <StorefrontIcon />,
    },
    {
      label: t('Custom Email Address'),
      content: (
        <CustomEmailTab
          selection={selection.custom}
          onAdd={(entry) =>
            setSelection({
              ...selection,
              custom: [...selection.custom, entry], // Add custom email
            })
          }
          onRemove={(entry) =>
            setSelection({
              ...selection,
              custom: selection.custom.filter((e) => e.email !== entry.email), // Remove custom email
            })
          }
        />
      ),
      icon: <AlternateEmailIcon />,
    },
  ];

  // Handle selecting emails for each entity type
  function handleCompanysSelect(data, element) {
    if (element && (!element.emails || !element.emails.length)) {
      handleFeedback(t('No E-Mail address found'), 'error');
      return;
    }

    if (element) {
      openEmailDialog(element, 'company'); // Open email dialog for companies
    } else {
      const newSelection = {
        ...selection,
        company: keepOnlySelectedEntities('company', data),
      };

      setSelection(newSelection);
      setPreSelected(newSelection);
    }
  }

  function handleContactsSelect(data, element) {
    if (element && (!element.emails || !element.emails.length)) {
      handleFeedback(t('No E-Mail address found'), 'error');
      return;
    }

    if (element) {
      openEmailDialog(element, 'contact'); // Open email dialog for contacts
    } else {
      const newSelection = {
        ...selection,
        contact: keepOnlySelectedEntities('contact', data),
      };

      setSelection(newSelection);
      setPreSelected(newSelection);
    }
  }

  function handleEmployeesSelect(data) {
    const newSelection = {
      ...selection,
      employee: data, // Update selected employees
    };

    setSelection(newSelection);
    setPreSelected(newSelection);
  }

  function handleLeadPersonsSelect(data, element) {
    if (element && (!element.emails || !element.emails.length)) {
      handleFeedback(t('No E-Mail address found'), 'error');
      return;
    }

    if (element) {
      openEmailDialog(element, 'leadPerson'); // Open email dialog for lead persons
    } else {
      const newSelection = {
        ...selection,
        leadPerson: keepOnlySelectedEntities('leadPerson', data),
      };

      setSelection(newSelection);
      setPreSelected(newSelection);
    }
  }

  function handleLeadCompaniesSelect(data, element) {
    if (element && (!element.emails || !element.emails.length)) {
      handleFeedback(t('No E-Mail address found'), 'error');
      return;
    }

    if (element) {
      openEmailDialog(element, 'leadCompany'); // Open email dialog for lead companies
    } else {
      const newSelection = {
        ...selection,
        leadCompany: keepOnlySelectedEntities('leadCompany', data),
      };

      setSelection(newSelection);
      setPreSelected(newSelection);
    }
  }

  // Handle saving selected emails
  function handleEmailSelect(selectedEmails) {
    if (!elementSave) return;

    updateEntityEmails(
      elementSave.type,
      elementSave.element,
      selectedEmails, // Update selected emails
    );
  }

  useEffect(() => {
    generateCCDeliveryData(); // Generate final CC delivery data
  }, [selection]);

  function generateCCDeliveryData() {
    const newDeliveryCCData = [];
    selection.company.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: element.name,
        type: 'company',
        id: element.id,
      });
    });

    selection.contact.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: `${element.salutation || ''} ${element.firstName || ''} ${
          element.lastName || ''
        }`.trim(),
        type: 'contact',
        id: element.id,
      });
    });

    selection.employee.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: `${element.salutation || ''} ${element.firstName || ''} ${
          element.lastName || ''
        }`.trim(),
        type: 'employee',
        id: element.id,
      });
    });

    selection.leadPerson.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: `${element.salutation || ''} ${element.firstName || ''} ${
          element.lastName || ''
        }`.trim(),
        type: 'leadPerson',
        id: element.id,
      });
    });
    selection.leadCompany.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: element.name,
        type: 'leadCompany',
        id: element.id,
      });
    });
    selection.custom.forEach((element) => {
      newDeliveryCCData.push({
        email: element.email,
        name: element.name || element.email,
        type: 'custom',
      });
    });
    setDeliveryCCData(newDeliveryCCData); // Set the generated CC data
  }

  function handleSave() {
    onSave(deliveryCCData); // Pass final selected data to parent
    onCancel(); // Close dialog
  }

  function handleDelete(item) {
    const newSelection = [...selection[item.type]];
    const index =
      item.type === 'custom'
        ? newSelection.findIndex((obj) => obj.email === item.email)
        : newSelection.findIndex(
            (obj) => obj.id === item.id && obj.email === item.email,
          );
    if (index >= 0) {
      newSelection.splice(index, 1); // Remove item from selection
    }
    const updatedSelection = {
      ...selection,
      [item.type]: newSelection,
    };
    setSelection(updatedSelection); // Update selection after deletion
    setPreSelected(updatedSelection); // Update pre-selected data
  }

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  return (
    <Box className="flex flex-col h-full justify-between gap-2 overflow-hidden">
      <Box className="flex flex-col h-full gap-2 overflow-hidden pt-2">
        <LabeledBox label={label}>
          <Box
            className="overflow-y-auto flex flex-wrap gap-1"
            sx={{ maxHeight: '100px' }}
          >
            {deliveryCCData.length === 0 && (
              <Typography className="opacity-50" fontSize={14}>
                {t('No Mails Selected')}
              </Typography>
            )}
            {deliveryCCData.map((item, index) => (
              <Chip
                key={`${item.type}-${item.id || item.email}-${item.email}-${index}`}
                label={
                  item.name && item.name !== item.email
                    ? `${item.name} <${item.email}>`
                    : item.email
                }
                onDelete={() => handleDelete(item)} // Delete selected email
                size="small"
              />
            ))}
          </Box>
        </LabeledBox>
        <ExoTabs tabs={tabs} />
      </Box>
      <Box className="flex justify-between p-2">
        <Button variant="outlined" color="error" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button variant="contained" color="success" onClick={handleSave}>
          {t('Save')}
        </Button>
      </Box>
      <ExoDialog open={Boolean(nameValueData.array)} limitWidth>
        <SelectFromNameValueArray
          label="Select email"
          array={nameValueData.array}
          nameKey={nameValueData.nameKey}
          valueKey={nameValueData.valueKey}
          multiple
          selectedValues={nameValueData.selectedValues || []}
          onSelect={handleEmailSelect} // Handle email selection
          onCancel={() => {
            setNameValueData({});
            setElementSave(null);
          }}
        />
      </ExoDialog>
      <Feedback state={alertState} setState={setAlertState} />
    </Box>
  );
};

const CustomEmailTab = ({ selection, onAdd, onRemove }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  function handleAdd() {
    if (!isValidEmail) return;
    if (selection.some((e) => e.email === email)) return;
    onAdd({ name: name || email, email });
    setName('');
    setEmail('');
  }

  return (
    <Box className="flex flex-col gap-3 p-2 w-full">
      <TextField
        label={t('Name') + ` (${t('optional')})`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
      />
      <Box className="flex gap-2 w-full">
        <TextField
          label={t('E-Mail')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          variant="contained"
          color="success"
          onClick={handleAdd}
          disabled={!isValidEmail}
          sx={{ minWidth: 'unset', px: 2 }}
        >
          +
        </Button>
      </Box>
      {selection.length > 0 && (
        <Box className="flex flex-wrap gap-1 mt-1">
          {selection.map((entry) => (
            <Chip
              key={entry.email}
              label={
                entry.name && entry.name !== entry.email
                  ? `${entry.name} <${entry.email}>`
                  : entry.email
              }
              onDelete={() => onRemove(entry)}
              size="small"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TypeMailSelect;
